import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import RichTextEditor from "./RichTextEditor";
import { marked } from "marked";
import TurndownService from "turndown";
import { toast } from "sonner";
import {
  Loader2, Sparkles, FileText, RefreshCw, Copy, Download, ChevronRight, AlertTriangle, CheckCircle2, Send, Mail,
} from "lucide-react";

// Configure marked + turndown once
marked.setOptions({ gfm: true, breaks: true });
const turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
});
turndown.escape = (s) => s; // don't escape every special char — markdown stays clean

const mdToHtml = (md) => marked.parse(md || "");
const htmlToMd = (html) => turndown.turndown(html || "");

// Used by the side-nav for ordering / labels — backend `doc_order` overrides these at runtime.
const FALLBACK_ORDER = [
  "client_intake_brief",
  "assessment_summary",
  "audit_snapshot",
  "lead_funnel_snapshot",
  "automation_opportunity_map",
  "sprint_charter",
  "data_request_checklist",
  "follow_up_email",
  "internal_prep_sheet",
  "proposal_notes",
];

export default function DocumentsPanel({ interviewId, interview }) {
  const [bundle, setBundle] = useState({ documents: {}, doc_order: FALLBACK_ORDER, doc_titles: {} });
  const [activeKey, setActiveKey] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [draftHtml, setDraftHtml] = useState(""); // local working HTML for the active doc
  const [loaded, setLoaded] = useState(false);

  // === Send Packet dialog state ===
  const DEFAULT_PACKET_KEYS = ["follow_up_email", "audit_snapshot"];
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({
    recipient_email: "",
    recipient_name: "",
    subject: "",
    intro_message: "",
    doc_keys: DEFAULT_PACKET_KEYS,
    include_calendly: true,
  });

  // Track when the active doc changed (so we know to swap content into the editor)
  const lastLoadedDocKeyRef = useRef(null);
  const saveTimerRef = useRef(null);

  const docs = bundle.documents || {};
  const order = (bundle.doc_order && bundle.doc_order.length ? bundle.doc_order : FALLBACK_ORDER);
  const titles = bundle.doc_titles || {};
  const generatedCount = Object.keys(docs).length;
  const totalCount = order.length;

  const loadDocs = useCallback(async () => {
    try {
      const r = await api.get(`/interviews/${interviewId}/documents`);
      setBundle(r.data);
      // Auto-select first generated doc if nothing is active
      if (!lastLoadedDocKeyRef.current && Object.keys(r.data.documents || {}).length) {
        const first = (r.data.doc_order || FALLBACK_ORDER).find((k) => r.data.documents[k]);
        if (first) {
          setActiveKey(first);
          setDraftHtml(mdToHtml(r.data.documents[first].content_markdown || ""));
          lastLoadedDocKeyRef.current = first;
        }
      }
    } finally {
      setLoaded(true);
    }
  }, [interviewId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // When the active document changes, load its markdown into the editor.
  // Done in the click handler (not in an effect) so React's set-state-in-effect rule passes.
  const selectDoc = useCallback((key) => {
    setActiveKey(key);
    const d = (bundle.documents || {})[key];
    if (d) {
      setDraftHtml(mdToHtml(d.content_markdown || ""));
      lastLoadedDocKeyRef.current = key;
    }
  }, [bundle.documents]);

  // Autosave (debounced) when draftHtml changes
  const queueAutosave = (html) => {
    setDraftHtml(html);
    if (!activeKey) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSavingKey(activeKey);
      try {
        const md = htmlToMd(html);
        const r = await api.put(`/interviews/${interviewId}/documents/${activeKey}`, {
          content_markdown: md,
        });
        setBundle((prev) => ({ ...prev, documents: { ...prev.documents, [activeKey]: r.data } }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Save failed");
      } finally {
        setSavingKey(null);
      }
    }, 900);
  };

  const generateAll = async () => {
    if (generatedCount > 0 && !window.confirm(
      "This will regenerate ALL documents and overwrite any unsaved edits. Continue?"
    )) return;
    setGeneratingAll(true);
    try {
      const r = await api.post(`/interviews/${interviewId}/documents/generate`);
      // Background task started — poll list every 3s until generation completes
      const expectedTotal = r.data?.expected_total ?? FALLBACK_ORDER.length;
      toast.info(`Generating ${expectedTotal} documents in parallel — this takes 60-180 seconds.`);
      const maxPolls = 80; // ~4 min ceiling
      for (let i = 0; i < maxPolls; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const lr = await api.get(`/interviews/${interviewId}/documents`);
          setBundle({
            documents: lr.data.documents || {},
            doc_order: lr.data.doc_order,
            doc_titles: lr.data.doc_titles,
          });
          const status = lr.data.document_status || "";
          if (status.startsWith("draft") || status.startsWith("partial") || status.startsWith("error")) {
            // Generation finished — refresh the active doc's editor content
            const docs = lr.data.documents || {};
            const firstKey = (lr.data.doc_order || FALLBACK_ORDER).find((k) => docs[k]);
            const targetKey = activeKey && docs[activeKey] ? activeKey : firstKey;
            if (targetKey && docs[targetKey]) {
              setActiveKey(targetKey);
              setDraftHtml(mdToHtml(docs[targetKey].content_markdown || ""));
              lastLoadedDocKeyRef.current = targetKey;
            }
            if (status.startsWith("error")) {
              toast.error(`Generation failed: ${status.slice(6).slice(0, 160)}`);
            } else if (status.startsWith("partial")) {
              toast.warning(`Some docs failed (${status}). Try regenerate individually.`);
            } else {
              toast.success(`Generated ${lr.data.generated_count} of ${lr.data.expected_total} documents.`);
            }
            break;
          }
        } catch {
          // Network blip — keep polling
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start generation");
    } finally {
      setGeneratingAll(false);
    }
  };

  const regenerateOne = async (key) => {
    setRegeneratingKey(key);
    try {
      // Kick off the background regen job — POST returns almost instantly with
      // {status:'processing'}. Heavy docs (Opus 4.7) can take up to ~2 min so we
      // poll the per-doc status endpoint instead of blocking on the POST response.
      await api.post(`/interviews/${interviewId}/documents/${key}/regenerate`);
      let regenerated = null;
      const MAX_POLLS = 90; // 90 * 2s = 3 min ceiling
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const s = await api.get(`/interviews/${interviewId}/documents/${key}/regenerate/status`);
          if (s.data.status === "ready" && s.data.finished_at) {
            regenerated = s.data.document;
            break;
          }
          if (s.data.status === "failed") {
            throw new Error(s.data.error || "Regenerate failed");
          }
        } catch (pollErr) {
          if (pollErr?.message === "Regenerate failed") throw pollErr;
          // transient poll failure — keep polling
        }
      }
      if (!regenerated) throw new Error("Regeneration timed out — please try again.");
      setBundle((prev) => ({ ...prev, documents: { ...prev.documents, [key]: regenerated } }));
      if (key === activeKey) {
        setDraftHtml(mdToHtml(regenerated.content_markdown || ""));
        lastLoadedDocKeyRef.current = key;
      }
      toast.success(`Regenerated ${titles[key] || key}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Regenerate failed");
    } finally {
      setRegeneratingKey(null);
    }
  };

  const copyMarkdown = async () => {
    const d = docs[activeKey];
    if (!d) return;
    const md = htmlToMd(draftHtml);
    try {
      await navigator.clipboard.writeText(md);
      toast.success("Markdown copied to clipboard.");
    } catch {
      toast.error("Clipboard blocked. Use the Download button instead.");
    }
  };

  const downloadMarkdown = () => {
    const d = docs[activeKey];
    if (!d) return;
    const md = htmlToMd(draftHtml);
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeKey}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPdfBlob = async (path, fallbackName) => {
    try {
      const r = await api.get(path, { responseType: "blob", timeout: 60000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Server sets Content-Disposition; browsers honor it for blob URLs that came from XHR
      // only if we pass the same name. We extract the suggested filename from the response header.
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF download failed");
    }
  };

  const downloadActivePdf = () => {
    if (!activeKey) return;
    return downloadPdfBlob(
      `/interviews/${interviewId}/documents/${activeKey}/pdf`,
      `${activeKey}.pdf`,
    );
  };

  const downloadAllPdf = () =>
    downloadPdfBlob(
      `/interviews/${interviewId}/documents/pdf`,
      "goldline-clarity-packet.pdf",
    );

  // === Send Packet handlers ===
  const openSendDialog = () => {
    const generatedKeys = order.filter((k) => docs[k]);
    const defaultPicks = DEFAULT_PACKET_KEYS.filter((k) => generatedKeys.includes(k));
    const business = interview?.business_name || interview?.client_name || "your business";
    setSendForm({
      recipient_email: interview?.client_email || "",
      recipient_name: interview?.client_name || "",
      subject: `${business} — Your Clarity Document Packet`,
      intro_message: "",
      doc_keys: defaultPicks.length ? defaultPicks : generatedKeys.slice(0, 2),
      include_calendly: true,
    });
    setSendOpen(true);
  };

  const toggleSendKey = (k) => {
    setSendForm((f) => {
      const has = f.doc_keys.includes(k);
      return {
        ...f,
        doc_keys: has ? f.doc_keys.filter((x) => x !== k) : [...f.doc_keys, k],
      };
    });
  };

  const sendPacket = async () => {
    const email = (sendForm.recipient_email || "").trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid recipient email.");
      return;
    }
    if (!sendForm.doc_keys.length) {
      toast.error("Select at least one document to send.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(
        `/interviews/${interviewId}/documents/send-packet`,
        {
          recipient_email: email,
          recipient_name: sendForm.recipient_name || "",
          subject: sendForm.subject || undefined,
          intro_message: sendForm.intro_message || undefined,
          doc_keys: sendForm.doc_keys,
          include_calendly: !!sendForm.include_calendly,
        },
        { timeout: 60000 }
      );
      toast.success(`Packet sent to ${email}. (${r.data.doc_keys?.length || 0} docs attached as PDF.)`);
      setSendOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send packet.");
    } finally {
      setSending(false);
    }
  };

  const active = activeKey ? docs[activeKey] : null;

  return (
    <div className="bg-white border border-navy/10 p-8 md:p-10" data-testid="documents-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-gold">Goldline Document Packet</div>
          <h2 className="font-display text-3xl text-navy mt-2">Generated Documents</h2>
          <div className="w-12 h-[2px] bg-gold mt-3" />
          <p className="text-sm text-navy/60 mt-3 max-w-2xl">
            Draft client-ready and internal materials from the reviewed intake, transcript, and assessment.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {generatedCount > 0 && (
            <Button
              onClick={downloadAllPdf}
              variant="outline"
              className="rounded-none border-navy/30 text-navy hover:bg-ivory h-12 px-5"
              data-testid="download-all-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download All as PDF
            </Button>
          )}
          {generatedCount > 0 && (
            <Button
              onClick={openSendDialog}
              variant="outline"
              className="rounded-none border-navy text-navy hover:bg-ivory h-12 px-5"
              data-testid="send-packet-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Send to Client
            </Button>
          )}
          <Button
            onClick={generateAll}
            disabled={generatingAll}
            className="bg-navy hover:bg-navy/90 text-ivory rounded-none h-12 px-6"
            data-testid="generate-all-docs-btn"
          >
            {generatingAll ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating 10 docs (60-120s)…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> {generatedCount > 0 ? "Regenerate Document Packet" : "Generate Document Packet"}</>
            )}
          </Button>
        </div>
      </div>

      {/* Last-sent banner */}
      {interview?.last_packet_sent_at && (
        <div className="mt-4 flex items-center gap-2 text-xs text-navy/55" data-testid="last-packet-sent-banner">
          <Mail className="w-3.5 h-3.5 text-gold" />
          Packet last sent {new Date(interview.last_packet_sent_at).toLocaleString()}
          {interview.client_email ? ` to ${interview.client_email}` : ""}.
        </div>
      )}

      {loaded && generatedCount === 0 && (
        <div className="mt-8 p-6 bg-ivory border-l-4 border-gold flex items-start gap-3" data-testid="empty-docs-state">
          <FileText className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div className="text-sm text-navy/75">
            <p className="font-semibold text-navy">Nothing generated yet.</p>
            <p className="mt-1">Click <strong>Generate Document Packet</strong> above to produce all 10 consulting documents from this intake&apos;s data. Generation runs in parallel and usually takes 60–120 seconds.</p>
          </div>
        </div>
      )}

      {generatedCount > 0 && (
        <div className="mt-8 grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Side nav */}
          <nav className="space-y-1" data-testid="docs-sidenav">
            {order.map((k, idx) => {
              const d = docs[k];
              const isActive = k === activeKey;
              const isGenerated = !!d;
              const isRegen = regeneratingKey === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => selectDoc(k)}
                  disabled={!isGenerated}
                  data-testid={`doc-nav-${k}`}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition ${
                    isActive
                      ? "bg-navy text-ivory"
                      : isGenerated
                        ? "bg-ivory text-navy hover:border-gold border border-transparent hover:border"
                        : "text-navy/30 cursor-not-allowed"
                  }`}
                >
                  <span className={`text-[10px] font-mono font-semibold ${isActive ? "text-gold" : "text-navy/40"}`}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 truncate text-xs leading-tight">{titles[k] || k}</span>
                  {isRegen && <Loader2 className="w-3 h-3 animate-spin" />}
                  {isActive && !isRegen && <ChevronRight className="w-3 h-3" />}
                </button>
              );
            })}
            <div className="text-[10px] uppercase tracking-widest text-navy/30 mt-4 px-3" data-testid="doc-progress">
              {generatedCount} of {totalCount} generated
            </div>
          </nav>

          {/* Active doc editor */}
          {active ? (
            <div className="min-w-0" data-testid="doc-editor">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                  <h3 className="font-display text-2xl text-navy">{active.title}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-navy/40">
                    <Badge variant="outline" className="rounded-none text-[10px] tracking-wider uppercase font-semibold border-navy/20">
                      {active.status || "draft"}
                    </Badge>
                    {savingKey === activeKey ? (
                      <span className="inline-flex items-center gap-1 text-gold">
                        <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                      </span>
                    ) : (
                      <span>Last edited {active.updated_at ? new Date(active.updated_at).toLocaleString() : "—"}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={copyMarkdown}
                    className="rounded-none border-navy/20 text-navy hover:bg-ivory text-xs h-8"
                    data-testid="doc-copy-md-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy Markdown
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadMarkdown}
                    className="rounded-none border-navy/20 text-navy hover:bg-ivory text-xs h-8"
                    data-testid="doc-download-md-btn"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download .md
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadActivePdf}
                    className="rounded-none border-navy/20 text-navy hover:bg-ivory text-xs h-8"
                    data-testid="doc-download-pdf-btn"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download PDF
                  </Button>
                  <Button
                    onClick={() => regenerateOne(activeKey)}
                    disabled={regeneratingKey === activeKey}
                    className="bg-gold hover:bg-gold/90 text-navy rounded-none text-xs h-8"
                    data-testid="doc-regen-btn"
                  >
                    {regeneratingKey === activeKey ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Regenerating…</>
                    ) : (
                      <><RefreshCw className="w-3 h-3 mr-1" /> Regenerate</>
                    )}
                  </Button>
                </div>
              </div>

              <RichTextEditor
                key={activeKey}
                value={draftHtml}
                onChange={queueAutosave}
                placeholder="Generate this document or start writing…"
                minHeight={420}
              />

              <div className="mt-4 flex items-start gap-2 text-[11px] text-navy/40 italic">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Generated outputs are draft consulting materials and should be reviewed by Goldline before being shared with clients.
              </div>
            </div>
          ) : (
            <div className="text-sm text-navy/50" data-testid="doc-empty-active">
              Pick a document from the side menu.
            </div>
          )}
        </div>
      )}

      {generatedCount === totalCount && (
        <div className="mt-6 flex items-center gap-2 text-sm text-gold" data-testid="all-docs-generated-banner">
          <CheckCircle2 className="w-4 h-4" /> All {totalCount} documents generated.
        </div>
      )}

      {/* Send Packet Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-ivory border border-navy/15 rounded-none max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="send-packet-dialog">
          <DialogHeader>
            <div className="eyebrow text-gold">Send to Client</div>
            <DialogTitle className="font-display text-3xl text-navy">Email the Document Packet</DialogTitle>
            <DialogDescription className="text-sm text-navy/60 mt-1">
              A branded PDF of the selected documents will be attached. Replies route back to your email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gold font-semibold">Recipient Email</label>
                <Input
                  data-testid="send-packet-email"
                  type="email"
                  value={sendForm.recipient_email}
                  onChange={(e) => setSendForm((f) => ({ ...f, recipient_email: e.target.value }))}
                  placeholder="client@example.com"
                  className="mt-1 rounded-none border-navy/15 focus-visible:ring-gold"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gold font-semibold">Recipient Name</label>
                <Input
                  data-testid="send-packet-name"
                  value={sendForm.recipient_name}
                  onChange={(e) => setSendForm((f) => ({ ...f, recipient_name: e.target.value }))}
                  placeholder="First Last"
                  className="mt-1 rounded-none border-navy/15 focus-visible:ring-gold"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gold font-semibold">Subject</label>
              <Input
                data-testid="send-packet-subject"
                value={sendForm.subject}
                onChange={(e) => setSendForm((f) => ({ ...f, subject: e.target.value }))}
                className="mt-1 rounded-none border-navy/15 focus-visible:ring-gold"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gold font-semibold">Intro Message (optional)</label>
              <Textarea
                data-testid="send-packet-intro"
                rows={3}
                value={sendForm.intro_message}
                onChange={(e) => setSendForm((f) => ({ ...f, intro_message: e.target.value }))}
                placeholder="A short personal note that appears above the document list in the email body."
                className="mt-1 rounded-none border-navy/15 focus-visible:ring-gold"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-wider text-gold font-semibold">Documents to include</label>
                <div className="text-[11px] text-navy/50" data-testid="send-packet-count">{sendForm.doc_keys.length} selected</div>
              </div>
              <div className="mt-2 border border-navy/10 divide-y divide-navy/10 bg-white">
                {order.map((k) => {
                  const d = docs[k];
                  if (!d) return null;
                  const checked = sendForm.doc_keys.includes(k);
                  return (
                    <label
                      key={k}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-ivory cursor-pointer"
                      data-testid={`send-packet-pick-${k}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleSendKey(k)}
                        data-testid={`send-packet-check-${k}`}
                      />
                      <span className="text-sm text-navy flex-1 truncate">{titles[k] || k}</span>
                      {DEFAULT_PACKET_KEYS.includes(k) && (
                        <Badge variant="outline" className="rounded-none text-[9px] tracking-wider border-gold text-gold uppercase">Default</Badge>
                      )}
                    </label>
                  );
                })}
                {order.filter((k) => docs[k]).length === 0 && (
                  <div className="px-3 py-3 text-xs text-navy/50">No generated documents yet. Generate first.</div>
                )}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-navy/70">
              <Checkbox
                checked={sendForm.include_calendly}
                onCheckedChange={(c) => setSendForm((f) => ({ ...f, include_calendly: !!c }))}
                data-testid="send-packet-calendly"
              />
              Include a "Book a follow-up call" link in the email body
            </label>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <Button
              variant="outline"
              onClick={() => setSendOpen(false)}
              disabled={sending}
              className="rounded-none border-navy/20 text-navy hover:bg-white"
              data-testid="send-packet-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={sendPacket}
              disabled={sending || sendForm.doc_keys.length === 0}
              className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold"
              data-testid="send-packet-submit"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Packet</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
